/**
 * CREATE YOUTUBE AD GROUPS — /google-ads/youtube/create/adgroup[?campaign_id=…]
 *
 * One YouTube (Demand Gen) campaign, MANY ad group drafts (add / duplicate /
 * remove), created in order. Each draft picks where it may show (in-stream,
 * in-feed, Shorts) and its locations and languages — the same ad-group shape
 * the campaign builder creates. Standard Video campaigns are read-only through
 * the Google Ads API, so they are not offered here.
 *
 * Publishing is RESUMABLE: created groups are remembered by draft id, so a
 * mid-way rejection can be fixed and re-submitted without duplicates.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import toast from 'react-hot-toast';
import { CheckCircle2, Circle, Copy, Layers, Plus, Trash2, XCircle } from 'lucide-react';
import {
  googleAdsService, GoogleAdsCampaignSummary, GoogleAdsGeoSuggestion, GoogleAdsLanguage,
} from '@/services/google-ads.service';
import {
  BackTo, Checkbox, ErrorBox, errMsg, FORMATS, Label, Spinner, Tag, useYoutubeAuth, YT_BASE,
} from '@/components/google-ads/youtube/youtubeShared';
import { LanguageSearch, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';

const uid = () => Math.random().toString(36).slice(2, 10);

type GroupDraft = {
  id: string;
  name: string;
  formats: string[];
  locations: GoogleAdsGeoSuggestion[];
  languages: GoogleAdsLanguage[];
};

const newGroup = (n: number): GroupDraft => ({
  id: uid(), name: `Ad group ${n}`, formats: FORMATS.map((f) => f.v), locations: [], languages: [],
});
const cloneGroup = (g: GroupDraft): GroupDraft => ({
  ...g, id: uid(), name: g.name ? `${g.name} (copy)` : '',
  formats: [...g.formats], locations: [...g.locations], languages: [...g.languages],
});
const formatLabel = (v: string) => FORMATS.find((f) => f.v === v)?.l ?? v;

type P = 'idle' | 'running' | 'done' | 'failed';
const StepIcon = ({ s }: { s?: P }) =>
  s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
  : s === 'running' ? <Spinner size={15} className="text-primary" />
  : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
  : <Circle size={15} className="shrink-0 text-text-muted/40" />;

export default function CreateYouTubeAdgroup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { accountUid } = useYoutubeAuth();

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[]>([]);
  const [standardVideo, setStandardVideo] = useState(0);
  const [campaignId, setCampaignId] = useState(params.get('campaign_id') || '');

  const first = useMemo(() => newGroup(1), []);
  const [groups, setGroups] = useState<GroupDraft[]>([first]);
  const [openId, setOpenId] = useState<string>(first.id);
  const patch = (gid: string, p: Partial<GroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...p } : g)));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pubMap, setPubMap] = useState<Record<string, P>>({});
  const madeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.listCampaigns(accountUid)
      .then((list) => {
        const live = list.filter((c) => c.status !== 'REMOVED');
        setCampaigns(live.filter((c) => c.advertisingChannelType === 'DEMAND_GEN'));
        setStandardVideo(live.filter((c) => c.advertisingChannelType === 'VIDEO').length);
      })
      .catch(() => setCampaigns([]));
  }, [accountUid]);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!campaignId) i.push('Pick the campaign these ad groups belong to.');
    if (groups.length === 0) i.push('Add at least one ad group.');
    groups.forEach((g, n) => {
      if (!g.name.trim()) i.push(`Ad group ${n + 1} is missing its name.`);
      if (g.formats.length === 0) i.push(`${g.name || `Ad group ${n + 1}`}: pick at least one ad format.`);
    });
    return i;
  }, [campaignId, groups]);

  const submit = async () => {
    setError(null);
    if (!accountUid) return setError('Connect a Google Ads account first.');
    if (issues.length) return setError(issues[0]);

    setSaving(true);
    const mark = (k: string, v: P) => setPubMap((m) => ({ ...m, [k]: v }));
    try {
      for (const g of groups) {
        if (madeRef.current[g.id]) continue;
        mark(g.id, 'running');
        try {
          const res = await googleAdsService.youtube.createDemandGenAdGroup(accountUid, {
            campaignId, name: g.name.trim(),
            formats: g.formats.map((f) => f.replace('YOUTUBE_', '').toLowerCase()),
            geoTargetIds: g.locations.length ? g.locations.map((l) => String(l.id)) : undefined,
            languageIds: g.languages.length ? g.languages.map((l) => String(l.id)) : undefined,
          });
          madeRef.current[g.id] = res.adGroupId;
        } catch (e) {
          mark(g.id, 'failed'); setError(`${g.name}: ${errMsg(e)}`); return;
        }
        mark(g.id, 'done');
      }
      toast.success(groups.length === 1 ? 'Ad group created — paused, add its ads next.' : `${groups.length} ad groups created — paused, add their ads next.`);
      navigate(YT_BASE);
    } finally { setSaving(false); }
  };

  return (
    <>
      <Topbar
        title="New YouTube Ad Groups"
        breadcrumb="Google Ads / YouTube"
        description="Where each group's ads may show and who they reach — add as many groups as the campaign needs"
        back={<BackTo to={YT_BASE} label="Back to YouTube Ads" />}
      />
      <div className="w-full px-7 py-7">
        <div className="mx-auto w-full max-w-2xl space-y-4">
          <Card className="p-6">
            <FormField label="Campaign" required>
              <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                <option value="">Pick a YouTube campaign</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormField>
            {standardVideo > 0 && (
              <p className="mt-2 text-2xs text-text-muted">
                {standardVideo} standard Video campaign{standardVideo === 1 ? ' is' : 's are'} not listed — Google only lets them be changed at ads.google.com.
              </p>
            )}
          </Card>

          {groups.map((g, n) => {
            const open = openId === g.id;
            const made = !!madeRef.current[g.id];
            return (
              <Card key={g.id} padded={false} className="overflow-hidden">
                <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
                  <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    onClick={() => setOpenId(open ? '' : g.id)}>
                    <StepIcon s={pubMap[g.id]} />
                    <Layers size={15} className="shrink-0 text-text-muted" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-text-primary">{g.name || `Ad group ${n + 1}`}</div>
                      <div className="truncate text-2xs text-text-muted">
                        {g.formats.length === FORMATS.length ? 'All YouTube formats' : g.formats.map(formatLabel).join(', ') || 'No formats'}
                        {` · ${g.locations.length ? g.locations.map((l) => l.name).join(', ') : 'Everywhere'}`}
                        {g.languages.length ? ` · ${g.languages.map((l) => l.name).join(', ')}` : ''}
                      </div>
                    </div>
                  </button>
                  {(!g.name.trim() || g.formats.length === 0) && <Tag tone="warn">incomplete</Tag>}
                  {made && <Tag tone="ok">created</Tag>}
                  <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"
                    title="Duplicate ad group" aria-label="Duplicate ad group" disabled={saving}
                    onClick={() => {
                      const copy = cloneGroup(g);
                      setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), copy, ...gs.slice(i + 1)]; });
                      setOpenId(copy.id);
                    }}>
                    <Copy size={14} />
                  </button>
                  {groups.length > 1 && !made && (
                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
                      title="Remove ad group" aria-label="Remove ad group" disabled={saving}
                      onClick={() => setGroups((gs) => gs.filter((x) => x.id !== g.id))}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {open && (
                  <div className="space-y-4 p-4">
                    <div>
                      <Label>Ad group name</Label>
                      <Input className="mt-1.5" aria-label="Ad group name" placeholder="e.g. UAE — Shorts viewers" value={g.name} disabled={made}
                        onChange={(e) => patch(g.id, { name: e.target.value })} />
                    </div>

                    <div className="rounded-xl border border-border p-3.5">
                      <div className="text-xs font-semibold text-text-primary">Ad formats</div>
                      <p className="mb-2.5 mt-0.5 text-2xs text-text-muted">Where this group's ads may show on YouTube — any combination.</p>
                      <div className="space-y-2">
                        {FORMATS.map((f) => (
                          <label key={f.v} className="flex items-center gap-2.5 text-sm text-text-primary">
                            <Checkbox checked={g.formats.includes(f.v)}
                              onCheckedChange={() => !made && patch(g.id, { formats: g.formats.includes(f.v) ? g.formats.filter((x) => x !== f.v) : [...g.formats, f.v] })} />
                            {f.l}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-border p-3.5">
                      <div className="text-xs font-semibold text-text-primary">Locations</div>
                      <p className="mb-2.5 mt-0.5 text-2xs text-text-muted">Searched live from Google — countries, regions and cities. Empty = everywhere.</p>
                      <LocationSearch accountUid={accountUid} value={g.locations} onChange={(v) => patch(g.id, { locations: v })} />
                    </div>

                    <div className="rounded-xl border border-border p-3.5">
                      <div className="text-xs font-semibold text-text-primary">Languages</div>
                      <p className="mb-2.5 mt-0.5 text-2xs text-text-muted">Searched live from Google. Empty = all languages.</p>
                      <LanguageSearch accountUid={accountUid} value={g.languages} onChange={(v) => patch(g.id, { languages: v })} />
                    </div>

                    <div className="rounded-lg bg-primary-soft p-3 text-xs text-text-secondary">
                      Optimized targeting finds viewers likely to act within your selected locations and languages. Detailed audience segments can be adjusted in Google Ads after creation.
                    </div>
                  </div>
                )}
              </Card>
            );
          })}

          <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
            onClick={() => { const g = newGroup(groups.length + 1); setGroups((gs) => [...gs, g]); setOpenId(g.id); }}>
            Add another ad group
          </Button>

          {error && <ErrorBox message={error} />}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => navigate(YT_BASE)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} loading={saving} disabled={saving || issues.length > 0}>
              {error ? 'Retry' : groups.length === 1 ? 'Create ad group' : `Create ${groups.length} ad groups`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
